import { grey, red } from "@mui/material/colors";
import { formLabelClasses } from "@mui/material/FormLabel";
import { inputLabelClasses } from "@mui/material/InputLabel";
import {
  createTheme,
  type ThemeOptions as MuiThemeOptions,
  type PaletteColorOptions,
  type Transitions,
} from "@mui/material/styles";
import { hslToHex } from "./util/colors";

/**
 * The RecordPalette type is used to define the colours used for each Inventory
 * record type.
 */
export type RecordPalette = {
  bg: string;
  fg: string;
  lighter?: string;
};

/**
 * The colours for one of the Requests detail panel's action buttons: its text/icon colour,
 * border, and resting background (the hover background is computed from these, not stored here).
 */
export type ActionButtonColors = {
  color: string;
  borderColor: string;
  backgroundColor: string;
};

declare module "@mui/material/styles" {
  interface Theme {
    borders: {
      table?: string;
      descriptionList?: string;
      floatingActions?: string;
      menu?: string;
      section?: string;
      menuButton?: string;
      card?: string;
      themedDialog?: (hue: number, sat: number, lig: number) => string;
      themedDialogTitle?: (hue: number, sat: number, lig: number) => string;
    };
    transitions: {
      iconTransformations: string;
      filterToggle: string;
    } & Transitions;
  }

  interface SimplePaletteColorOptions {
    saturated?: string;
    placeholderText?: string;
    background?: string;
  }

  interface TransitionsOptions {
    iconTransformations: string;
    filterToggle: string;
  }
  interface ThemeOptions {
    borders?: {
      table: string;
      descriptionList: string;
      floatingActions: string;
      menu: string;
      section: string;
      menuButton: string;
      card: string;
      themedDialog: (hue: number, sat: number, lig: number) => string;
      themedDialogTitle: (hue: number, sat: number, lig: number) => string;
    };
  }
  interface PaletteColor {
    background: string;
    saturated?: string;
    placeholderText?: string;
  }
  interface Palette {
    tertiary: PaletteColor;
    callToAction: PaletteColor;
    standardIcon: PaletteColor;
    lightestGrey: string;
    warningRed: string;
    deletedGrey: string;
    modifiedHighlight: string;
    record: {
      container: RecordPalette;
      sample: RecordPalette;
      subSample: RecordPalette;
      sampleTemplate: RecordPalette;
      instrument: RecordPalette;
      instrumentTemplate: RecordPalette;
      document: RecordPalette;
      mixed: RecordPalette;
      attachment: Pick<RecordPalette, "fg">;
      gallery: Pick<RecordPalette, "fg">;
    };
    hover: {
      tableRow: string;
      iconButton: string;
    };
    sidebar: {
      selected: {
        bg: string;
        badge: string;
      };
    };
    // Specific to the Sample Requests feature's status chip backgrounds - not shared with any
    // other feature's colour scheme, unlike `warning`/`success`/`error`/`info` above, whose
    // auto-derived `.light` shades are both a different colour and not vetted for the WCAG AA
    // text contrast these were hand-picked for (see RequestsStatusChip.tsx).
    sampleRequestStatus: {
      pending: string;
      approved: string;
      fulfilled: string;
      rejected: string;
    };
    // The Requests detail panel's own approve/reject/fulfil action buttons - also specific to
    // this feature, not shared with the generic `warning`/`success`/`error`/`info` palette.
    sampleRequestActions: {
      reject: ActionButtonColors;
      approve: ActionButtonColors;
      approvedDisabled: ActionButtonColors;
      markAsFulfilled: ActionButtonColors;
    };
  }
  interface PaletteOptions {
    callToAction: PaletteColorOptions;
    tertiary: PaletteColorOptions;
    hover: {
      tableRow: string;
      iconButton: string;
    };
    borders?: {
      card?: string;
    };
    record: {
      container: RecordPalette;
      sample: RecordPalette;
      subSample: RecordPalette;
      sampleTemplate: RecordPalette;
      instrument: RecordPalette;
      instrumentTemplate: RecordPalette;
      document: RecordPalette;
      mixed: RecordPalette;
      attachment: Pick<RecordPalette, "fg">;
      gallery: Pick<RecordPalette, "fg">;
    };
    standardIcon: PaletteColorOptions;
    sidebar: {
      selected: {
        bg: string;
        badge: string;
      };
    };
    sampleRequestStatus: {
      pending: string;
      approved: string;
      fulfilled: string;
      rejected: string;
    };
    sampleRequestActions: {
      reject: ActionButtonColors;
      approve: ActionButtonColors;
      approvedDisabled: ActionButtonColors;
      markAsFulfilled: ActionButtonColors;
    };
    lightestGrey: string;
    menuIconGrey: string;
    faIconGrey: string;
    modifiedHighlight: string;
    warningRed: string;
    deletedGrey: string;
  }
  interface TypeBackground {
    alt?: string;
  }
  interface TypographyVariants {
    letterSpacing: {
      spaced: string;
      dense: string;
    };
  }

  interface TypographyVariantsOptions {
    letterSpacing?: {
      spaced: string;
      dense: string;
    };
  }
}

/*
 * Various components in MUI have a `color` prop which can be used to change
 * the colour of the component. This prop is typed as a string, and the
 * available colours are defined in the default theme. However, it is often
 * useful to be able to use custom colours that extend the default theme.  In
 * the theme we define some additional colours, such as `callToAction` and as
 * these are available anywhere within the scope of our theme, they can be used
 * in the `color` prop of components such as Buttons. However, the TypeScript
 * type of the `color` prop does not allow for these custom colours so we need
 * to extend the interfaces that define the colours that these components
 * accept.
 */
declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    standardIcon: true;
    callToAction: true;
  }
}
declare module "@mui/material/IconButton" {
  interface IconButtonPropsColorOverrides {
    standardIcon: true;
  }
}
declare module "@mui/material/Badge" {
  interface BadgePropsColorOverrides {
    callToAction: true;
  }
}
declare module "@mui/material/Switch" {
  interface SwitchPropsColorOverrides {
    tertiary: true;
  }
}
declare module "@mui/material/Chip" {
  interface ChipPropsColorOverrides {
    callToAction: true;
  }
}
declare module "@mui/material/Fab" {
  interface FabPropsColorOverrides {
    callToAction: true;
  }
}

/**
 * These colours are defined in a way that can be adjusted where they are
 * needed; made darker or reduced contrast depending upon the situation.
 */
export const COLORS = {
  primary: {
    hue: 196.57,
    saturation: 100,
    lightness: 46.86,
  },
};

const baseTheme = createTheme({
  palette: {
    primary: {
      main: hslToHex(COLORS.primary.hue, COLORS.primary.saturation, COLORS.primary.lightness),
      contrastText: "#fff",
      saturated: "#009ad6",
      placeholderText: "#8babcb",
      background: "#00adef22",
      dark: "rgba(0, 121, 167, 1)",
      light: "rgba(51, 189, 242, 1)",
    },
    callToAction: {
      main: hslToHex(207, 76, 76),
      contrastText: "#264b58",
      saturated: "#ff0000",
      placeholderText: "#8babcb",
      background: hslToHex(207, 76, 66),
      dark: hslToHex(207, 76, 66),
      light: "rgba(51, 189, 242, 1)",
    },
    secondary: {
      main: "#f50057",
      light: "#fc6da0",
      contrastText: "#fff",
    },
    tertiary: {
      main: "#fb8c00",
      light: "#ffcc80",
      contrastText: "#000",
    },
    background: {
      default: "#fafafa",
      alt: "#fff",
    },
    error: {
      main: "#d32f2f",
    },
    warning: {
      main: "#f57c00",
    },
    info: {
      main: "#2196f3",
      contrastText: "#0d3c61",
    },
    hover: {
      tableRow: "rgba(0,0,0,0.04)",
      iconButton: "rgba(0,0,0,0.04)",
    },
    record: {
      container: {
        fg: "#2196f3",
        bg: "#4175AA",
        lighter: "#e6edf4",
      },
      sample: {
        fg: "#4caf50",
        bg: "#509554",
        lighter: "#e9f2e9",
      },
      subSample: {
        fg: "#21b07c",
        bg: "#256F5C",
        lighter: "#e9f1ef",
      },
      sampleTemplate: {
        fg: "#651ef5",
        bg: "#6B5C8A",
        lighter: "#ecebf0",
      },
      instrument: {
        fg: "#f57c00",
        bg: "#ab4c08",
        lighter: "#fce8d5",
      },
      instrumentTemplate: {
        fg: "#651ef5",
        bg: "#6B5C8A",
        lighter: "#ecebf0",
      },
      document: {
        bg: "#af5076",
        fg: "#d16691",
      },
      mixed: {
        bg: "#77858d",
        fg: "#fafafa",
        lighter: "#ecedef",
      },
      attachment: {
        fg: "#e64a19",
      },
      gallery: {
        fg: "#a768c6",
      },
    },
    standardIcon: {
      main: "rgba(0,0,0,0.54)",
    },
    sidebar: {
      selected: {
        bg: "#e6e6e6",
        badge: "#f7f7f7",
      },
    },
    sampleRequestStatus: {
      pending: "#FDF0DF",
      approved: "#E7EEF2",
      fulfilled: "#E6F1E8",
      rejected: "#FBEAE8",
    },
    sampleRequestActions: {
      reject: { color: "#C62828", borderColor: "#C4726B", backgroundColor: "white" },
      approve: { backgroundColor: "#0B72A3", borderColor: "#0B72A3", color: "white" },
      approvedDisabled: { color: "#4F6E7C", borderColor: "#C2D6E0", backgroundColor: "#EDF4F8" },
      markAsFulfilled: { color: "#3A4A52", borderColor: "#7D8D95", backgroundColor: "white" },
    },

    // the lightest grey on a white background that has AA contrast
    lightestGrey: "#949494",
    menuIconGrey: "#616161",
    faIconGrey: "#8d8d8d",
    modifiedHighlight: "teal",
    warningRed: red[500],
    deletedGrey: grey[700],
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 770,
      lg: 1150,
      xl: 1920,
    },
  },
  borders: {
    table: "1px solid rgba(224, 224, 224, 1)",
    descriptionList: "1px solid rgba(224, 224, 224, 1)",
    floatingActions: "1px solid rgba(224, 224, 224, 1)",
    menu: "1px solid #d3d4d5",
    section: "2px solid rgb(235, 235, 235)",
    menuButton: "1px solid rgba(192 ,192, 192, 1)",
    card: "1px solid rgba(0, 0, 0, 0.12)",
    themedDialog: (hue: number, sat: number, lig: number) => `3px solid hsl(${hue} ${sat}% ${lig}% / 25%)`,
    themedDialogTitle: (hue: number, sat: number, lig: number) => `1px solid hsl(${hue} ${sat}% ${lig}% / 20%)`,
  },
  transitions: {
    iconTransformations: "transform 0.3s cubic-bezier(0.42, 0, 0.94, 1.49) 0s", // just a little bounce
    filterToggle: "filter .2s ease-in-out, opacity .2s ease-in-out",
  },
  typography: {
    h6: {
      fontSize: "1.1rem",
    },
    letterSpacing: {
      spaced: "0.03em",
      dense: "0.01071em",
    },
  },
});

/**
 * The standard theme used across the application.
 */
export default createTheme({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  // biome-ignore lint/suspicious/noExplicitAny: initial biome migration
  ...(baseTheme as any as MuiThemeOptions),
  components: {
    MuiFormLabel: {
      styleOverrides: {
        root: {
          cursor: "text !important",
          color: "rgb(10,10,10)",
          marginBottom: baseTheme.spacing(0.25),
          fontWeight: 600,
          fontSize: "0.95em",
          [`&.${inputLabelClasses.outlined}`]: {
            fontWeight: "initial",
            fontSize: "1.1em",
          },
          [`&.${formLabelClasses.focused}`]: {
            color: "rgb(10,10,10)",
          },
        },
        asterisk: {
          color: baseTheme.palette.error.main,
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          cursor: "default",
          "&:focus-visible": {
            outline: `2px solid ${baseTheme.palette.primary.main}`,
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        color: "standardIcon",
      },
      styleOverrides: {
        containedCallToAction: {
          fontWeight: 700,
          "&&": {
            color: baseTheme.palette.callToAction.contrastText,
            "@media (prefers-contrast: more), (forced-colors: active)": {
              backgroundColor: "black",
              color: "white",
              "&:hover": {
                backgroundColor: "black",
              },
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          /*
           * For some unknown reason, MuiSelects that follow HTMLLabelElements
           * in the DOM have a margin-top when no other input component does,
           * thereby mis-aligning MuiSelects when rendered next to other such
           * components.
           */
          marginTop: "0 !important",
        },
        select: {
          cursor: "default",
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          cursor: "text !important",
        },
      },
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          cursor: "default",
          "&:focus-visible": {
            outline: `2px solid ${baseTheme.palette.primary.main}`,
            borderRadius: baseTheme.spacing(0.5),
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        deleteIcon: {
          cursor: "default",
        },
        clickable: {
          cursor: "default",
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        subtitle1: {
          fontSize: "1.05rem",
          letterSpacing: "0.02em",
        },
        body1: {
          letterSpacing: "0.03em",
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        displayedRows: {
          margin: 0,
        },
      },
    },
  },
});
